import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFileExcel,
  faFilePdf,
  faDesktop,
  faGlobe,
  faEye,
  faClock,
  faUser,
  faSpinner,
} from "@fortawesome/free-solid-svg-icons";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import SearchBar from "../../components/ui/SearchBar";
import TableWrapper from "../../components/ui/TableWrapper";
import EmptyState from "../../components/ui/EmptyState";
import Pagination from "../../components/ui/Pagination";
import DateFilter from "../../components/ui/DateFilter";
import { useUserLogs } from "../../hooks";

const UserLogs = () => {
  const navigate = useNavigate();

  const {
    userLogs,
    pagination,
    loading,
    error,
    searchTerm,
    setSearchTerm,
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    refresh,
  } = useUserLogs();

  const [isFilterApplied, setIsFilterApplied] = useState(false);

  useEffect(() => {
    setIsFilterApplied(Boolean(startDate && endDate));
  }, [startDate, endDate]);

  const handleSearchChange = (value) => {
    setSearchTerm(value);
    setCurrentPage(1);
  };

  const handleViewLog = (log) => {
    const userlogId = log.userlogId;
    navigate(`/app/userLogs/${userlogId}`);
  };

  const handleExportExcel = () => {
    console.log("Exporting logs to Excel...");
  };

  const handleExportPDF = () => {
    console.log("Exporting logs to PDF...");
  };

  const totalItems = pagination?.total ?? userLogs.length;
  const totalPages = pagination?.last_page ?? 1;
  const startIndex = (currentPage - 1) * pageSize;

  return (
    <div className="flex flex-col gap-6">
      {/* Header */}
      <Card className="bg-white border border-gray-200 px-4 sm:px-6 py-4 shadow-sm">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center">
              <FontAwesomeIcon
                icon={faClock}
                className="text-purple-800 h-5 w-5"
              />
            </div>
            <div>
              <h2 className="text-xl sm:text-2xl font-semibold text-gray-900">
                User Logs
              </h2>
              <p className="text-sm text-gray-600 mt-0.5">
                Login and logout records for all users
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              className="gap-2 border-gray-300 hover:border-blue-800"
              onClick={handleExportExcel}
            >
              <FontAwesomeIcon
                icon={faFileExcel}
                className="text-green-600 h-4 w-4"
              />
              Excel
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="gap-2 border-gray-300 hover:border-blue-800"
              onClick={handleExportPDF}
            >
              <FontAwesomeIcon
                icon={faFilePdf}
                className="text-red-600 h-4 w-4"
              />
              PDF
            </Button>
          </div>
        </div>
      </Card>

      {/* Date Filter */}
      <DateFilter
        startDate={startDate}
        endDate={endDate}
        onStartDateChange={setStartDate}
        onEndDateChange={setEndDate}
        onApply={() => {
          setIsFilterApplied(true);
          setCurrentPage(1);
          refresh();
        }}
        onClear={() => {
          setStartDate("");
          setEndDate("");
          setIsFilterApplied(false);
          setCurrentPage(1);
          refresh();
        }}
        isFilterApplied={isFilterApplied}
        totalItems={totalItems}
        label="logs"
      />

      {/* Filters */}
      <Card className="bg-white border border-gray-200 px-4 py-3 shadow-sm">
        <SearchBar
          value={searchTerm}
          onChange={handleSearchChange}
          placeholder="Search by name, username, device, or browser..."
          className="w-full"
        />
      </Card>

      {/* Table */}
      <TableWrapper
        title="logs"
        totalItems={totalItems}
        currentPage={currentPage}
        totalPages={totalPages}
        className="bg-white border border-gray-200 shadow-sm"
      >
        <Table>
          <TableHeader>
            <TableRow className="bg-gray-50 hover:bg-gray-50 border-b-2 border-gray-200">
              <TableHead className="text-xs font-semibold text-gray-600 uppercase tracking-wider py-2.5 px-3">
                S/N
              </TableHead>
              <TableHead className="text-xs font-semibold text-gray-600 uppercase tracking-wider py-2.5 px-3">
                <FontAwesomeIcon icon={faUser} className="mr-1.5 h-3 w-3" />
                Full Name
              </TableHead>
              <TableHead className="text-xs font-semibold text-gray-600 uppercase tracking-wider py-2.5 px-3">
                Username
              </TableHead>
              <TableHead className="text-xs font-semibold text-gray-600 uppercase tracking-wider py-2.5 px-3">
                <FontAwesomeIcon icon={faClock} className="mr-1.5 h-3 w-3" />
                Login Time
              </TableHead>
              <TableHead className="text-xs font-semibold text-gray-600 uppercase tracking-wider py-2.5 px-3">
                <FontAwesomeIcon icon={faClock} className="mr-1.5 h-3 w-3" />
                Logout Time
              </TableHead>
              <TableHead className="text-xs font-semibold text-gray-600 uppercase tracking-wider py-2.5 px-3">
                <FontAwesomeIcon icon={faDesktop} className="mr-1.5 h-3 w-3" />
                Device
              </TableHead>
              <TableHead className="text-xs font-semibold text-gray-600 uppercase tracking-wider py-2.5 px-3">
                <FontAwesomeIcon icon={faGlobe} className="mr-1.5 h-3 w-3" />
                Browser
              </TableHead>
              <TableHead className="text-xs font-semibold text-gray-600 uppercase tracking-wider py-2.5 px-3 text-center">
                Action
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-8">
                  <FontAwesomeIcon
                    icon={faSpinner}
                    spin
                    className="h-5 w-5 text-blue-800"
                  />
                  <p className="text-sm text-gray-500 mt-2">Loading logs...</p>
                </TableCell>
              </TableRow>
            ) : error ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-8">
                  <EmptyState
                    title="Failed to load logs"
                    message={error?.message || "Please try again later"}
                  />
                </TableCell>
              </TableRow>
            ) : userLogs.length > 0 ? (
              userLogs.map((log, idx) => (
                <TableRow
                  key={log.id}
                  className="hover:bg-blue-50/30 transition-colors duration-150 border-b border-gray-100"
                >
                  <TableCell className="py-2.5 px-3 text-sm text-gray-600">
                    {startIndex + idx + 1}
                  </TableCell>
                  <TableCell className="py-2.5 px-3 text-sm font-medium text-gray-900">
                    {log.full_name || "—"}
                  </TableCell>
                  <TableCell className="py-2.5 px-3 text-sm text-gray-600">
                    {log.username}
                  </TableCell>
                  <TableCell className="py-2.5 px-3 text-sm text-gray-600 whitespace-nowrap">
                    {log.login_time}
                  </TableCell>
                  <TableCell className="py-2.5 px-3 text-sm text-gray-600 whitespace-nowrap">
                    {log.logout_time ? (
                      log.logout_time
                    ) : (
                      <Badge className="bg-green-600 text-white border-green-200">
                        Active
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell className="py-2.5 px-3 text-sm text-gray-600 capitalize">
                    {log.device_type}
                  </TableCell>
                  <TableCell className="py-2.5 px-3 text-sm text-gray-600">
                    {log.browser}
                  </TableCell>
                  <TableCell className="py-2.5 px-3 text-center">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleViewLog(log)}
                      className="gap-1 border-gray-300 hover:border-blue-800 hover:bg-blue-50"
                    >
                      <FontAwesomeIcon icon={faEye} className="h-3 w-3" />
                      View
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-8">
                  <EmptyState
                    title="No logs found"
                    message={
                      searchTerm || isFilterApplied
                        ? "Try adjusting your search or filters"
                        : "No logs available"
                    }
                  />
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableWrapper>

      {/* Pagination */}
      {totalItems > 0 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          pageSize={pageSize}
          totalItems={totalItems}
          onPageChange={setCurrentPage}
          onPageSizeChange={setPageSize}
        />
      )}
    </div>
  );
};

export default UserLogs;